import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import AgentListAPI from '../api/AgentList/AgentListAPI';
import { useAuth } from './AuthContext';

const EXCLUDED_AGENT_NAME = 'Eva Supervisor Agent';

function normalizeAgents(response) {
  const filteredAgents = (Array.isArray(response) ? response : []).filter((agent) => {
    return (agent?.name || '').trim().toLowerCase() !== EXCLUDED_AGENT_NAME.toLowerCase();
  });

  return [...filteredAgents].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * AgentListContext provides agent list state and controlled methods for managing agents.
 */
export const AgentListContext = createContext();

/**
 * AgentListProvider wraps children and supplies agent list state.
 */
export const AgentListProvider = ({ children }) => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Core fetcher for the agent list, guarded only against overlapping requests.
  const fetchAgents = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const response = await AgentListAPI();
      if (isMountedRef.current) {
        setAgents(normalizeAgents(response));
        setHasLoaded(true);
      }
    } catch (error) {
      console.error('AgentListContext: Failed to fetch agents list:', error);
    } finally {
      isFetchingRef.current = false;
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Single fetcher for the agent list. Idempotent: guarded on an access token
  // and a fetch flag so hosts that trigger it explicitly (e.g. the mini-app,
  // where the MSAL user stays null) never cause a duplicate request.
  const loadAgents = useCallback(async () => {
    const accessToken = sessionStorage.getItem('access_token');

    if (!accessToken || hasLoaded) {
      return;
    }

    await fetchAgents();
  }, [hasLoaded, fetchAgents]);

  // Forces a re-fetch regardless of the `hasLoaded` guard so admin mutations
  // (publish/delete) can keep the shared picker list in sync without a remount.
  const refreshAgents = useCallback(async () => {
    const accessToken = sessionStorage.getItem('access_token');

    if (!accessToken) {
      return;
    }

    await fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents, user]);

  // Hosts like the mini-app write `access_token` after mount while the MSAL
  // user stays null, so neither `loadAgents` nor `user` changes to retry the
  // guarded fetch above. Same-tab sessionStorage writes also don't emit a
  // `storage` event, so poll for token readiness and retry once it appears.
  useEffect(() => {
    if (hasLoaded || sessionStorage.getItem('access_token')) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (sessionStorage.getItem('access_token')) {
        clearInterval(intervalId);
        loadAgents();
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, [loadAgents, hasLoaded]);

  const value = {
    agents,
    loadAgents,
    refreshAgents,
    isLoading,
    hasLoaded
  };

  return (
    <AgentListContext.Provider value={value}>
      {children}
    </AgentListContext.Provider>
  );
};

export const useAgentList = () => {
  const context = React.useContext(AgentListContext);
  if (!context) {
    throw new Error('useAgentList must be used within AgentListProvider');
  }
  return context;
};
